import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { Button } from "@/components/ui/button";
import { Eye, MoreHorizontal, ExternalLink, User, UserCheck, Radio, Calendar, FileImage, Download, Maximize2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { colors } from "@/utils/colors";
import { LeadTrackingItem } from "@/Api/leadTracking";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";

interface TableDataProps {
  dataApi: {
    data: LeadTrackingItem[];
    current_page: number;
    last_page: number;
  };
  pageActions: {
    next: () => void;
    previous: () => void;
    lastPage: () => void;
  };
}

export const TableData = ({ dataApi, pageActions }: TableDataProps) => {
  const [showModal, setShowModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState<LeadTrackingItem | null>(null);
  const [showFileModal, setShowFileModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState<string | null>(null);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    }).format(date);
  };

  const handleShow = (item: LeadTrackingItem) => {
    setSelectedItem(item);
    setShowModal(true);
  };

  const handleShowFile = (fileUrl: string) => {
    setSelectedFile(fileUrl);
    setShowFileModal(true);
  };

  return (
    <>
      <Table className="bg-zinc-50 rounded dark:bg-[#2c2c2c]">
        <TableHeader>
          <TableRow>
            <TableHead>Cliente</TableHead>
            <TableHead>Vendedor</TableHead>
            <TableHead>Mídias</TableHead>
            <TableHead>Cadastrado por</TableHead>
            <TableHead>Data/Hora</TableHead>
            <TableHead className="text-center">Arquivo</TableHead>
            <TableHead className="text-center">Ações</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {dataApi?.data?.map((item) => (
            <TableRow key={item.id}>
              <TableCell className="font-medium">{item.client_name}</TableCell>
              <TableCell>{item.seller_name}</TableCell>
              <TableCell>{item.media_name}</TableCell>
              <TableCell>{item.created_by_name}</TableCell>
              <TableCell>{formatDate(item.created_at)}</TableCell>
              <TableCell className="text-center">
                {item.file ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleShowFile(item.file!)}
                  >
                    <ExternalLink className="h-4 w-4 text-zinc-600 dark:text-zinc-300" />
                  </Button>
                ) : (
                  <span className="text-gray-400">—</span>
                )}
              </TableCell>
              <TableCell className="text-center">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleShow(item)}
                >
                  <Eye className="h-4 w-4 text-zinc-600 dark:text-zinc-300" />
                </Button>
              </TableCell>
            </TableRow>
          ))}
          {(!dataApi?.data || dataApi.data.length < 1) && (
            <TableRow>
              <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                Nenhum registro encontrado.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <div className="flex justify-between mb-3">
        {dataApi?.data?.length > 0 && (
          <span className="font-medium">
            Exibindo {dataApi?.current_page} de um total de {dataApi?.last_page}
          </span>
        )}
        {dataApi?.last_page > 1 && (
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  href="#"
                  className={cn("h-8", colors.bg_primary)}
                  onClick={pageActions.previous}
                />
              </PaginationItem>
              <PaginationItem>
                <PaginationLink
                  href="#"
                  className={cn(
                    `size-8 bg-transparent border-2 border-[#023859] text-slate-900 font-bold cursor-auto hover:bg-transparent`
                  )}
                >
                  <span className="pointer-events-none dark:text-[#F4F4F5]">
                    {dataApi?.current_page}
                  </span>
                </PaginationLink>
              </PaginationItem>
              <PaginationItem>
                <PaginationLink
                  href="#"
                  className="text-black h-8 hover:bg-stone-950/10"
                  onClick={pageActions.lastPage}
                >
                  <MoreHorizontal className="h-4 w-4 dark:text-[#F4F4F5]" />
                </PaginationLink>
              </PaginationItem>
              <PaginationItem>
                <PaginationNext
                  href="#"
                  className={cn("h-8", colors.bg_primary)}
                  onClick={pageActions.next}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        )}
      </div>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader className="pb-2">
            <DialogTitle className="text-xl font-semibold flex items-center gap-2">
              <User className="h-5 w-5 text-primary" />
              Detalhes do Lead
            </DialogTitle>
          </DialogHeader>
          {selectedItem && (
            <div className="space-y-4">
              <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/30 dark:to-indigo-950/30 rounded-lg border border-blue-100 dark:border-blue-900">
                <div className="h-14 w-14 rounded-full bg-primary flex items-center justify-center border-2 border-white shadow-md">
                  <span className="text-white text-lg font-semibold">
                    {selectedItem.client_name?.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {selectedItem.client_name}
                  </h3>
                  <Badge variant="secondary" className="mt-1">
                    <Radio className="h-3 w-3 mr-1" />
                    {selectedItem.media_name}
                  </Badge>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-start gap-3 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                  <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-full">
                    <UserCheck className="h-4 w-4 text-green-600 dark:text-green-400" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Vendedor</p>
                    <p className="font-medium text-gray-900 dark:text-gray-100">{selectedItem.seller_name}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                  <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-full">
                    <User className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Cadastrado por</p>
                    <p className="font-medium text-gray-900 dark:text-gray-100">{selectedItem.created_by_name}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg col-span-2">
                  <div className="p-2 bg-orange-100 dark:bg-orange-900/30 rounded-full">
                    <Calendar className="h-4 w-4 text-orange-600 dark:text-orange-400" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Data/Hora do Cadastro</p>
                    <p className="font-medium text-gray-900 dark:text-gray-100">{formatDate(selectedItem.created_at)}</p>
                  </div>
                </div>
              </div>

              {selectedItem.file && (
                <>
                  <Separator />
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <FileImage className="h-4 w-4 text-gray-500" />
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Arquivo anexado</span>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSelectedFile(selectedItem.file!);
                            setShowFileModal(true);
                          }}
                          className="gap-2"
                        >
                          <Maximize2 className="h-4 w-4" />
                          Visualizar
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => window.open(selectedItem.file!, "_blank")}
                          className="gap-2"
                        >
                          <Download className="h-4 w-4" />
                          Baixar
                        </Button>
                      </div>
                    </div>
                    <div className="relative rounded-lg overflow-hidden border bg-gray-100 dark:bg-gray-800">
                      <img
                        src={selectedItem.file}
                        alt="Arquivo do lead"
                        className="w-full max-h-64 object-contain"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                          target.parentElement!.innerHTML = `
                            <div class="flex flex-col items-center justify-center h-32 text-gray-400">
                              <svg class="h-12 w-12 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                              <span class="text-sm">Clique em "Baixar" para visualizar</span>
                            </div>
                          `;
                        }}
                      />
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={showFileModal} onOpenChange={setShowFileModal}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Arquivo do Cadastro</DialogTitle>
          </DialogHeader>
          {selectedFile && (
            <div className="flex justify-center">
              <img
                src={selectedFile}
                alt="Arquivo do cadastro"
                className="max-h-[70vh] object-contain rounded"
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
